import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle, WhatsappLogo } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { ProgramsBlock } from "../components/marketing/BnspPrograms";
import { Wordmark } from "../components/marketing/Wordmark";
import { LangToggle } from "../components/marketing/Header";
import { useLanguage } from "../context/LanguageContext";
import { WHATSAPP_URL, EMAIL, PHONE_DISPLAY } from "../i18n/content";

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const { t } = useLanguage();
  const service = t.services.items.find((s) => s.id === serviceId);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) document.title = `${service.title} — PT Darma Daya Guna`;
    return () => {
      document.title = "PT Darma Daya Guna — Training & Consultant";
    };
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-brand-paper font-satoshi flex flex-col items-center justify-center gap-6 px-6" data-testid="service-not-found">
        <Wordmark size="text-4xl" />
        <p className="text-black/60">{t.servicePage.notFound}</p>
        <Link
          data-testid="service-not-found-back"
          to="/"
          className="flex items-center gap-2 bg-brand-ink text-brand-paper px-6 py-3 font-cabinet font-bold hover:bg-brand-green transition-colors duration-300"
        >
          <ArrowLeft size={16} weight="bold" />
          {t.servicePage.back}
        </Link>
      </div>
    );
  }

  const others = t.services.items.filter((s) => s.id !== service.id);

  return (
    <div className="grain bg-brand-paper text-brand-ink font-satoshi min-h-screen" data-testid="service-detail-page">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-brand-paper/85 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="service-header-logo" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-brand-green" aria-label="PT Darma Daya Guna — home">
            <Wordmark size="text-3xl" />
          </Link>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link
              data-testid="service-back-link"
              to="/"
              className="hidden sm:flex items-center gap-2 text-sm font-semibold text-black/60 hover:text-black transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              <ArrowLeft size={15} weight="bold" />
              {t.servicePage.back}
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-12 md:pb-16">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-black/45 mb-8">
              <Link to="/" className="hover:text-black transition-colors">DDG</Link>
              <span className="mx-2 text-brand-red">/</span>
              {t.nav.services}
              <span className="mx-2 text-brand-red">/</span>
              <span className="text-brand-green">{service.title}</span>
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="flex items-start gap-6 md:gap-10">
              <span className="font-cabinet font-black text-6xl md:text-8xl leading-none text-stroke select-none">
                {service.num}
              </span>
              <h1 data-testid="service-detail-title" className="font-cabinet font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                {service.title}
              </h1>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p data-testid="service-detail-desc" className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-black/60">
              {service.desc}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="service-detail-tags">
              {service.tags.map((tag) => (
                <span key={tag} className="border border-black/20 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-black/70">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Image */}
        {service.image && (
          <Reveal className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="relative overflow-hidden aspect-[21/9] max-h-[420px] w-full">
              <img src={service.image} alt={service.title} className="w-full h-full object-cover grayscale" />
              <div className="absolute inset-0 bg-brand-green/15 mix-blend-multiply" />
            </div>
          </Reveal>
        )}

        {/* Detail + points */}
        <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-14 md:py-20 grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-7">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-brand-red mb-6">— {t.services.label}</p>
            <p className="text-base md:text-lg leading-relaxed text-black/65">{service.detail}</p>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="border border-black/15 bg-white p-7 md:p-9">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/45 mb-5">
                {t.services.pointsLabel}
              </p>
              <ul className="flex flex-col gap-4">
                {service.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm md:text-base text-black/75">
                    <CheckCircle size={20} weight="duotone" className="text-brand-green shrink-0 mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </section>

        {/* Certification programs (BNSP) */}
        {service.programs && (
          <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-14 md:pb-20" data-testid="service-programs">
            <Reveal>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-brand-red mb-6">
                — {t.services.programsLabel}
              </p>
              <ProgramsBlock programs={service.programs} />
            </Reveal>
          </section>
        )}

        {/* CTA */}
        <section className="border-y border-black/10 bg-white">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-14 md:py-20 flex flex-col md:flex-row md:items-center gap-8 justify-between">
            <h2 className="font-cabinet font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-xl">
              {t.contact.title}
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link
                data-testid="service-page-cta"
                to="/#contact"
                className="group flex items-center gap-3 bg-brand-green text-white px-7 py-4 font-cabinet font-bold tracking-wide hover:bg-brand-ink transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
              >
                {t.servicePage.cta}
                <ArrowUpRight size={17} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <a
                data-testid="service-page-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 border border-black/25 px-7 py-4 font-cabinet font-bold tracking-wide text-black/80 hover:border-brand-green hover:text-brand-green transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                <WhatsappLogo size={20} weight="duotone" />
                {t.contact.whatsappCta}
              </a>
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-black/45 mb-8">
            — {t.servicePage.related}
          </p>
          <div className="flex flex-col border-t border-black/10">
            {others.map((s) => (
              <Link
                key={s.id}
                data-testid={`related-service-${s.id}`}
                to={`/layanan/${s.id}`}
                className="group flex items-center justify-between gap-6 py-6 border-b border-black/10 transition-colors duration-300 hover:bg-black/[0.02] px-2 md:px-4 focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                <div className="flex items-center gap-6">
                  <span className="font-cabinet font-black text-2xl text-stroke">{s.num}</span>
                  <span className="font-cabinet font-bold text-lg md:text-xl tracking-tight group-hover:text-brand-green transition-colors duration-300">
                    {s.title}
                  </span>
                </div>
                <ArrowUpRight size={20} weight="bold" className="text-black/30 transition-[color,transform] duration-300 group-hover:text-brand-red group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-brand-ink text-brand-paper">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row gap-6 md:items-center justify-between">
          <p className="text-sm text-white/60 max-w-md leading-relaxed">{t.footer.tagline}</p>
          <div className="text-sm text-white/60 flex flex-col gap-1">
            <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors duration-300">{EMAIL}</a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">{PHONE_DISPLAY}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
